<template>
    <v-container fluid fill-height>    
        <v-layout v-if="loading" align-center justify-center>
            <v-progress-circular indeterminate></v-progress-circular>
        </v-layout>
        <v-layout v-else>
            <v-flex xs12>
                <v-card>
                    <v-toolbar color="primary" class="white--text" flat>
                        <v-toolbar-title>Pest Configuration</v-toolbar-title>
                        <v-spacer></v-spacer>
                        <v-icon dark>layers</v-icon>
                    </v-toolbar>
                    <v-container class="pt-1" fluid grid-list-md>
                        <v-layout row>
                            <v-flex xs12>
                                <v-alert :value="error != null" outline type="error">
                                    {{error}}
                                </v-alert>
                            </v-flex>
                        </v-layout>
                        <v-layout row wrap>                            
                            <v-flex xs4>
                                <v-autocomplete                                
                                    label="Category"
                                    item-value="ProductCategoryID"
                                    item-text="ProductCategoryName"
                                    :items="availableCategories"
                                    v-model="selectedCategoryIDs"                                    
                                    multiple
                                    hide-details
                                    clearable
                                    dense
                                    outline
                                    >
                                </v-autocomplete>
                            </v-flex>
                            <v-flex xs4>
                                <v-autocomplete                                
                                    label="Classification"
                                    :items="pestClassifications"
                                    item-value="PestClassificationID"
                                    item-text="Name"
                                    v-model="selectedClassification"
                                    hide-details
                                    clearable
                                    dense
                                    outline>
                                </v-autocomplete>
                            </v-flex> 
                            <v-flex xs4 v-if="selectedClassification && getType(selectedClassification).length > 0">
                                <v-autocomplete
                                    label="Type"
                                    :items="getType(selectedClassification)"
                                    item-value="PestClassificationTypeID"
                                    item-text="Name"
                                    v-model="selectedClassificationType"
                                    hide-details
                                    clearable
                                    dense
                                    outline>
                                </v-autocomplete>
                            </v-flex>
                        </v-layout>
                        <v-layout row wrap>
                            <v-flex xs4>
                                <v-text-field v-model="search" append-icon="search" label="Filter" single-line hide-details outline dense></v-text-field>
                            </v-flex>
                            <v-flex xs1>
                                <v-checkbox class="active-checkbox" label="Active Only" v-model="activeOnly" hide-details dense outlline></v-checkbox>
                            </v-flex>
                            <v-spacer></v-spacer>
                            <v-flex xs1>
                                <v-btn color="primary" block @click="showEditAddItem({})">
                                    Add
                                </v-btn>
                            </v-flex>
                        </v-layout>
                        <v-layout pt-3>
                            <v-flex xs12>
                                <hr />
                            </v-flex>                         
                        </v-layout>                        
                        <v-layout pt-2>
                            <v-flex xs12>
                                <v-data-table :headers="headers" :items="filteredItems" :search="search" :rows-per-page-items="rowsPerPageItems" :pagination.sync="pagination" must-sort>
                                    <v-progress-linear slot="progress" color="primary" indeterminate></v-progress-linear>
                                    <template slot="items" slot-scope="props">
                                        <td>{{props.item.Name}}</td>
                                        <td>{{props.item.Description}}</td>
                                        <td>{{props.item.TimeRequired ? 'Y' : 'N'}}</td>
                                        <td>{{props.item.AppliesInPlants}}</td>
                                        <td>{{props.item.Status ? 'Active' : 'Inactive'}}</td>
                                        <td>
                                            <v-btn color="primary" block @click="showEditAddItem(props.item)">
                                                Edit
                                            </v-btn>
                                        </td>
                                    </template>
                                </v-data-table>
                            </v-flex>
                        </v-layout>
                    </v-container>
                </v-card>
            </v-flex>
        </v-layout>
        <v-dialog v-model="showAddEditDialog" fullscreen hide-overlay transition="dialog-bottom-transition" @keydown.esc="cancelEdit">
            <v-card>
                <v-toolbar dark color="primary">
                    <v-btn icon dark :disabled="saving" @click="cancelEdit">
                        <v-icon large>close</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-toolbar-title v-if="editItem.PestConfigurationID">Edit Pest Configuration</v-toolbar-title>
                    <v-toolbar-title v-else>Add New Pest Configuration</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-toolbar-items>
                    </v-toolbar-items>
                </v-toolbar>
                <v-form ref="editForm" v-model="editValid">
                    <v-layout class="dialog-content">
                        <v-flex xs12 md6 offset-md3 mt-4>
                            <v-layout wrap>
                                <v-flex xs12>
                                    <v-text-field v-model="editItem.Name" :rules="[rules.required, rules.maxTwoHundredCharacters]" counter label="Name">
                                    </v-text-field>
                                </v-flex>
                                <v-flex xs12>
                                    <v-text-field v-model="editItem.Description" :rules="[rules.maxFiveHundredCharacters]" label="Description">
                                    </v-text-field>
                                </v-flex>
                                <v-flex xs12>
                                    <select-many-or-default-first
                                        :items="plants"
                                        item-text="PlantName"
                                        item-value="PlantID"
                                        chips
                                        multiple
                                        v-model="editItem.PlantIDs"
                                        label="Plants"
                                    ></select-many-or-default-first>
                                </v-flex>
                                <v-flex xs12>
                                    <v-autocomplete
                                        label="Product Categories"
                                        :items="availableCategories"
                                        item-value="ProductCategoryID"
                                        item-text="ProductCategoryName"
                                        v-model="editItem.ProductCategoryIDs"
                                        :rules="[rules.required]" 
                                        @change="categoryChanged"                                    
                                        chips 
                                        multiple                                        
                                        placeholder="Categories"
                                        >
                                    </v-autocomplete>
                                </v-flex>
                                <v-flex xs12>
                                    <v-autocomplete
                                        :readonly="filteredScales.length < 1"
                                        label="Scoring Scale"
                                        :items="filteredScales"
                                        item-value="ScoutingScoreScaleID"
                                        item-text="Description"
                                        v-model="editItem.ScoutingScoreScaleID"
                                        :rules="[rules.required]"
                                        placeholder="Scoring Scale"
                                        >
                                    </v-autocomplete>
                                </v-flex>
                                <v-flex xs12>
                                    <v-autocomplete
                                        label="Classification"
                                        :items="pestClassifications"
                                        item-value="PestClassificationID"
                                        item-text="Name"
                                        v-model="editItem.PestClassificationID"
                                        placeholder="Classifications"
                                        clearable
                                        >
                                    </v-autocomplete>
                                </v-flex>
                                <v-flex xs12 v-if="editItem.PestClassificationID && getType(editItem.PestClassificationID).length > 0">
                                    <v-autocomplete
                                        label="Classification Type"
                                        :items="getType(editItem.PestClassificationID)"
                                        item-value="PestClassificationTypeID"
                                        item-text="Name"
                                        v-model="editItem.PestClassificationTypeID"
                                        placeholder="Classification Types"
                                        clearable
                                        >
                                    </v-autocomplete>
                                </v-flex>
                                <v-flex xs12 v-if="editItem.PestConfigurationID">
                                    <v-layout row wrap class="pb-4">
                                        <v-flex class="mr-2" shrink v-for="(item, index) in editItem.Images" :key="index">
                                            <div class="item elevation-5">
                                                <v-container class="pa-0" fluid>
                                                    <v-layout row justify-space-between>
                                                        <v-flex shrink>
                                                            <v-btn icon color="error" @click="confirmDelete(item)">
                                                                <v-icon>delete</v-icon>
                                                            </v-btn>
                                                        </v-flex>
                                                        <v-spacer></v-spacer>
                                                        <v-flex shrink>
                                                            <v-btn icon color="primary" @click="showImageExpanded(item.photoURL)">
                                                                <v-icon>zoom_out_map</v-icon>
                                                            </v-btn>
                                                        </v-flex>
                                                    </v-layout>
                                                    <v-layout row>
                                                        <v-flex xs12>
                                                            <v-img class="ma-2" contain :src="item.photoURL" alt="Image" :lazy-src="require('@/assets/loading.png')" height="280px" width="280px" />
                                                        </v-flex>
                                                    </v-layout>
                                                </v-container>
                                            </div>
                                        </v-flex>
                                    </v-layout>
                                </v-flex>
                                <v-flex xs12>
                                    <v-btn :block="$vuetify.breakpoint.xs" color="primary" @click="selectNewImage()" :loading="processingPhoto">Add Image</v-btn>
                                    <input
                                        type="file"
                                        style="display: none"
                                        ref="newPhotoImage"
                                        accept="image/*"
                                        @change="handleNewImageSelectionChanged($event)"
                                    >                                   
                                </v-flex>
                                <v-flex xs12 v-for="(item, index) in newImageData" :key="index">
                                    <v-text-field
                                        v-model="item.newImageFilename"
                                        label="New Image"
                                        :disabled="true">
                                    </v-text-field>
                                    <img :id="'img-' + item.Name" class="new-image"/>
                                </v-flex>
                                <v-flex xs12>
                                    <v-alert :value="newImageProcessingError != null" outline type="error">
                                        {{newImageProcessingError}}
                                    </v-alert>
                                </v-flex>
                                <v-flex xs12>
                                    <v-checkbox v-model="editItem.TimeRequired" label="Time Required"></v-checkbox>
                                </v-flex>
                                <v-flex xs12>
                                    <v-checkbox v-model="editItem.Status" label="Status"></v-checkbox>
                                </v-flex>
                            </v-layout>
                            <v-layout wrap class="dialog-action-buttons">
                                <v-flex xs12 sm6>
                                    <v-btn block color="success" :disabled="!editValid || processingPhoto" :loading="saving" @click="save(editItem)">Save</v-btn>
                                </v-flex>
                                <v-flex xs12 sm6>
                                    <v-btn block color="error" :disabled="saving" @click="cancelEdit">Cancel</v-btn>
                                </v-flex>
                                <v-flex xs12>
                                    <v-alert :value="error != null" outline type="error">
                                        {{error}}
                                    </v-alert>
                                </v-flex>
                            </v-layout>
                        </v-flex>
                    </v-layout>
                </v-form>
            </v-card>
        </v-dialog>
        
        <delete-dialog
            :DisplayDialog="showConfirmDeleteImage" 
            :DialogTitle="'Delete Image'" 
            :DialogContents="'Are you sure you want to delete this image?'"
            @No="showConfirmDeleteImage = false"
            @Yes="deleteImage()">
        </delete-dialog>
        <v-dialog v-model="showImageDialog" fullscreen>
            <v-toolbar dark color="primary">
                <v-btn icon dark  @click="showImageDialog = false">
                    <v-icon large>close</v-icon>
                </v-btn>
                <v-spacer></v-spacer>
                <v-toolbar-title>Image</v-toolbar-title>                
                <v-spacer></v-spacer>
                <v-toolbar-items>
                </v-toolbar-items>
            </v-toolbar>
            <v-img v-if="imageSrc" contain :src="imageSrc" :lazy-src="require('@/assets/loading.png')"/>
        </v-dialog>
    </v-container>
</template>

<script>
import api from '@/api/pestConfigurationApi';
import scoutApi from '@/api/scoutApi';
import validationRules from "@/helpers/validationrules";
import UtilService from '@/services/utilService.js';
import SelectManyOrDefaultFirst from '@/components/generic/SelectManyOrDefaultFirst.vue';
import PlantApi from '@/api/plantApi'
import productCategoryApi from "@/api/productCategoryApi";
import { v4 as uuidv4 } from 'uuid';
import DeleteDialog from "@/components/generic/YesNoPopup.vue";

export default {
    components: {
        SelectManyOrDefaultFirst,
        DeleteDialog
    },
    data() {
        return {
            availableScoutingScoreScales: [],
            availableCategories: [],
            pestClassifications:[],
            selectedCategoryIDs: [],
            selectedClassification: null,
            selectedClassificationType: null,
            showConfirmDeleteImage: false,
            showImageDialog: false,
            imageSrc: null,
            items: [],
            editItem: {},
            loading: false,
            search: '',
            activeOnly: true,
            showAddEditDialog: false,
            rules: validationRules.rules,
            editValid: true,
            saving: false,
            error: null,
            rowsPerPageItems: [10, 25, 50, 100],
            pagination: {
                page: 1,
                rowsPerPage: 25,
                descending: false
            },
            headers: [{
                    text: "Name",
                    value: "Name",
                    align: 'left',
                },
                {
                    text: "Description",
                    value: "Description",
                    align: 'left'
                },
                {
                    text: "Time Required",
                    value: "TimeRequired",
                    align: 'left'
                },
                {
                    text: "Applies in Plants",
                    value: "AppliesInPlants",
                    align: 'left'
                },
                {
                    text: "Active Status",
                    value: "Status",
                    align: 'left'
                },
                {
                    text: "Edit",
                    value: "",
                    align: "left",
                    sortable: false
                }
            ],

            // image selection data
            processingPhoto: false,
            newImageData: [],
            newImageProcessingError: null,
            deleteItem: null,

            // image display
            imageAuthToken: null,

            // supporting data
            plants: []
        }
    },
    watch: {
        selectedClassification: {
            handler(newVal) {
                if(newVal === undefined) {
                    // handle the clearable values
                    this.selectedClassification = null;
                    this.selectedClassificationType = null;
                }
            }
        },
    },
    computed: {
        filteredItems() {
            let filtered = this.items.filter(c => c.Status || !this.activeOnly);

            if (this.selectedCategoryIDs.length > 0) {
                filtered = filtered.filter(c => c.ProductCategoryIDs.some(pc => this.selectedCategoryIDs.some(s => s == pc)));
            }

            if (this.selectedClassification) {
                filtered = filtered.filter(c => c.PestClassificationID == this.selectedClassification);
            }

            if (this.selectedClassificationType) {
                filtered = filtered.filter(c => c.PestClassificationTypeID == this.selectedClassificationType);
            }

            return filtered;
        },
        filteredScales() {
            if (this.editItem && this.editItem.ProductCategoryIDs && this.editItem.ProductCategoryIDs.length > 0) {
                return this.availableScoutingScoreScales.filter(s => s.ProductCategories.some(p => this.editItem.ProductCategoryIDs.some(e => e == p)));
            }

            return [];
        }
    },
    methods: {
        sampleHanlder() {
            console.log('value changed')
        },
        async loadItems() {
            this.loading = true;
            this.error = null;

            try {
                let pestConfigurations = await api.getPestConfigurations();

                pestConfigurations.forEach(config => {
                    config.Images.forEach(image => {
                        image.photoURL = this.formatPhotoURL(image.Name);
                    });                    
                });                

                this.items = pestConfigurations;
            } catch(err) {
                this.error = err;
            }

            this.loading = false;
        },
        categoryChanged() {
            if (this.filteredScales.length == 1) {
               this.editItem.ScoutingScoreScaleID = this.filteredScales[0].ScoutingScoreScaleID;
            }
        },
        showImageExpanded(url) {
            this.showImageDialog = true;
            this.imageSrc = url;
        },
        confirmDelete(item) {
            this.deleteItem = item;
            this.showConfirmDeleteImage = true;
        },
        deleteImage(){
            const index = this.editItem.Images.indexOf(this.deleteItem);
            if (index > -1) { // only splice array when item is found
                this.editItem.Images.splice(index, 1); // 2nd parameter means remove one item only
            }
            
            this.deleteItem = null;
            this.showConfirmDeleteImage = false;
        },
        async showEditAddItem(item) {
            this.editItem = JSON.parse(JSON.stringify(item));
            this.showAddEditDialog = true;
        },
        formatPhotoURL(photoName) {
            return process.env.VUE_APP_API_URI + '/pestconfigurations/photoData?' + [
                'photoName=' + photoName,
                'Token=' + this.imageAuthToken,
                'TimestampToStopTheCaching=' + Date.now()
            ].join('&');
        },
        async loadImageAuthToken(){
            this.imageAuthToken = await this.$store.dispatch("UserStore/acquireImageAuthToken");
        },
        selectNewImage(){
            if(this.processingPhoto){
                return;
            }

            this.$refs["newPhotoImage"].click();
        },
        async handleNewImageSelectionChanged(event){
            // for now, just store the image data in memory - we will only upload it if we save the changes to the pest configuration
            const data = await this.getFileData(event);                        
            this.newImageData.push(data);
            
            this.$nextTick(() => {
                const canvas = document.getElementById('img-' + data.Name);
                canvas.renderImage(data.Blob);
            });
        },
        getType(pestClassificationID) {
            if(!pestClassificationID) {
                return [];
            }

            return this.pestClassifications.find(p => p.PestClassificationID == pestClassificationID).ClassificationTypes.filter(ct => ct.IsActive);
        },
        async getFileData(event) {
            let photo = null;            
            this.newImageProcessingError = null;

            this.processingPhoto = true;

            const files = event.target.files;
            if(files[0] !== undefined) {
                if(files[0].name.lastIndexOf('.') > 0) {
                    let fileNameSplit = files[0].name.split('.');
                    let extension = fileNameSplit[fileNameSplit.length - 1];
                    let mimeType = files[0].type;

                    let image = await UtilService.loadFileToImage(files[0]);
                    let maxFileSize = 2 * 1024 * 1024;
                    let blob = await UtilService.resizeUntilBelow(image, mimeType, maxFileSize);

                    if(blob != null){
                        photo = {
                            Extension: extension,
                            MimeType: mimeType,
                            Blob: blob,
                            Name: uuidv4(),
                            newImageFilename: files[0].name
                        };
                    }
                    else {
                        this.newImageProcessingError = "Error processing image";
                    }
                }
            }

            this.processingPhoto = false;

            return photo;
        },
        async save(item) {
            this.saving = true;
            this.error = null;

            try {
                let response = {};

                if(item.PestConfigurationID) {
                    response = await api.updatePestConfiguration(item, this.newImageData);
                } else {
                    response = await api.addPestConfiguration(item, this.newImageData);
                }

                if(!response.Success) {
                    this.error = response.ErrorMessages.join(', ');
                    this.saving = false;
                    return;
                }
            } catch(err) {                
                this.error = err;
                console.log(err);
                this.saving = false;
                return;
            }          

            await this.loadItems();
            await this.cancelEdit();

            this.saving = false;
        },
        async cancelEdit() {
            this.editItem = {};

            // clear image related data
            this.newImageData= [];
            this.$refs["newPhotoImage"].value = null; // reset selector

            this.showAddEditDialog = false;
            this.error = null;
            this.$refs.editForm.reset();
        }
    },
    async created() {
        this.loading = true;

        this.plants = await PlantApi.getAllPlants();
        
        const productCategories = await productCategoryApi.getProductCategories();
        this.availableCategories = productCategories.map(x => {
            return {
                ProductCategoryID: x.ID,
                ProductCategoryName: x.Name
            };
        });

        const scoutingScoreScales = await scoutApi.getScoutingScoreScales();

        this.availableScoutingScoreScales = scoutingScoreScales.filter(s => s.IsActive).map(s =>{
            return {
                ScoutingScoreScaleID: s.ScoutingScoreScaleID,
                Description: `${s.Minimum} - ${s.Maximum} / ${s.Interval}`,
                ProductCategories: s.ProductCategories
            };
        })

        this.pestClassifications = await api.getPestClassifciations();
        this.pestClassifications = this.pestClassifications.filter(pc => pc.IsActive);

        await this.loadImageAuthToken();
        await this.loadItems();        
    }
}

HTMLImageElement.prototype.renderImage = function(blob){
  this.src = URL.createObjectURL(blob);
};

</script>
<style scoped>
.new-image {    
    width: 100px;
    height: 100px;
    object-fit: scale-down;
}
</style>
